import React, { useEffect, useState, useMemo } from "react";
import {
  FlatList,
  Text,
  TouchableOpacity,
  View,
  TextInput,
  ActivityIndicator,
  Platform,
} from "react-native";
import StandardNavigation from "../../../components/navigation/StandardNavigation";
import ProductItem from "../../../components/stock/ProductItem";
import MaterialIcons from "@expo/vector-icons/MaterialIcons";
import { useCameraPermissions } from "expo-camera";
import { Toast } from "react-native-toast-notifications";
import { useAudioPlayer } from "expo-audio";
import { LinearGradient } from "expo-linear-gradient";
import i18n from "../../../locales/i18n";
import Animated, { FadeInDown, LinearTransition } from "react-native-reanimated";
import { router, useFocusEffect } from "expo-router";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { appActions } from "../../../store/slices/appSlice";
import StockHeaderIcon from "../../../assets/stock/stock.svg";
import { useStock } from "../../../hooks/useStock";
import { StockSkeletonLoader } from "../../../components/stock/StockSkeletonLoader";
import WebStockScreen from "../../../components/web/WebStockScreen";

const StockScreen = React.memo(() => {
  const dispatch = useDispatch();
  const { locale } = useSelector((state: RootState) => state.app);

  // Use the custom stock hook
  const {
    products: allProducts,
    isLoading: stockLoading,
    error: stockError,
    fetchProducts,
    searchProducts,
    filterProducts,
    sortProducts,
    deleteProduct,
  } = useStock();

  // All state hooks should come first
  const [searchValue, setSearchValue] = useState("");
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("all"); // all, low_stock, high_value
  const [selectedSort, setSelectedSort] = useState("name_asc"); // name_asc, name_desc, price_asc, price_desc, stock_asc, stock_desc
  const [retryCount, setRetryCount] = useState(0);

  // Add timeout protection for loading state
  useEffect(() => {
    if (stockLoading) {
      const timeout = setTimeout(() => {
        if (stockLoading) {
          Toast.show("Loading is taking too long. Please try again.", {
            type: "warning",
          });
        }
      }, 15000); // 15 second timeout warning

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [stockLoading]);

  // Retry function
  const handleRetry = useCallback(() => {
    setRetryCount((prev) => prev + 1);
    fetchProducts(true);
  }, [fetchProducts, retryCount]);

  // All hook calls should come before any conditional returns
  const [permission, requestPermission] = useCameraPermissions();

  // Compute filtered and sorted data using useMemo for performance
  const displayedProducts = useMemo(() => {
    // Start with all products or search results
    let productsToDisplay = [];

    if (!searchValue || searchValue.trim() === "") {
      // If no search term, show all products
      productsToDisplay = allProducts;
    } else {
      // If there's a search term, use search function
      productsToDisplay = searchProducts(searchValue);
    }

    // Then apply filter
    const filteredProducts = filterProducts(productsToDisplay, selectedFilter);

    // Finally, sort the results
    return sortProducts(filteredProducts, selectedSort);
  }, [
    searchValue,
    selectedFilter,
    selectedSort,
    allProducts,
    searchProducts,
    filterProducts,
    sortProducts,
  ]);

  // Update locale when screen is focused
  useFocusEffect(
    useCallback(() => {
      dispatch(appActions.setLocale(i18n.locale));
    }, [dispatch])
  );

  // Fetch products when screen is focused
  useFocusEffect(
    useCallback(() => {
      fetchProducts(true); // Force refresh when focusing on stock page
    }, [fetchProducts])
  );

  const audioPlayer = useAudioPlayer(
    require("../../../assets/sounds/scan.mp3")
  );

  // Render web version on web platform - BEFORE camera permission checks
  if (Platform.OS === "web") {
    return <WebStockScreen />;
  }

  if (!permission) {
    // Camera permissions are still loading.
    return (
      <View className="flex-1 bg-white items-center justify-center">
        <Text className="mb-4">{i18n.t("cameraPermission")}</Text>
        <ActivityIndicator size="large" color="green" />
      </View>
    );
  }

  if (!permission.granted) {
    return (
      <View className="flex-1 items-center justify-center p-4">
        <Text className="text-center pb-4 text-lg text-gray-700">
          {i18n.t("cameraPermissionRequest")}
        </Text>
        <TouchableOpacity
          onPress={requestPermission}
          style={{ borderRadius: 9999 }}
        >
          <LinearGradient
            colors={["rgba(19, 121, 162, 1)", "rgba(5, 150, 208, 1)"]}
            style={{
              borderRadius: 9999,
              padding: 16,
            }}
          >
            <Text className="text-white text-center text-xl font-bold">
              {i18n.t("grantPermission")}
            </Text>
          </LinearGradient>
        </TouchableOpacity>
      </View>
    );
  }

  const handelSearch = (value: any) => {
    setSearchValue(value);
  };

  const handelSearchProduct = (productName: any) => {
    setSearchValue(productName);
  };

  const handleFilterChange = (filter: string) => {
    setSelectedFilter(filter);
    setShowFilterMenu(false);
  };

  const handleSortChange = (sort: string) => {
    setSelectedSort(sort);
    setShowSortMenu(false);
  };

  return (
    <View className="flex-1 bg-[#f5f5f5]">
      {/* Standard Navigation */}
      <StandardNavigation
        title={i18n.t("stock")}
        showBackButton={true}
        showMenuButton={true}
        showLogo={true}
        icon={<StockHeaderIcon width={30} height={30} />}
      />

      <View className="flex-1 items-center p-2.5 relative px-4 bg-[#f5f5f5]">
        {/* Close dropdowns when touching outside */}
        {(showFilterMenu || showSortMenu) && (
          <TouchableOpacity
            activeOpacity={1}
            className="absolute inset-0 w-full h-full z-[8]"
            onPress={() => {
              setShowFilterMenu(false);
              setShowSortMenu(false);
            }}
          />
        )}

        <TouchableOpacity
          className="absolute z-[5] bottom-[60px] right-5 rounded-[30px] mt-2.5 overflow-hidden"
          onPress={() => {
            router.push("/addproduct");
          }}
        >
          <View className="bg-[#434343] px-5 py-3 rounded-3xl flex-row justify-between items-center">
            <MaterialIcons name="add-circle" size={30} color="white" />
            <Text className=" text-lg font-[400]  text-white mx-2  ">
              {i18n.t("addproduct")}{" "}
            </Text>
          </View>
        </TouchableOpacity>

        {/* Search and Filter Section */}
        <View className="mt-4 w-full z-10 relative">
          {/* Search Bar with Filter Button */}
          <View
            className={`flex items-center shadow bg-[#EFEFEF] justify-between rounded-3xl ${
              locale == "fr" ? "flex-row-reverse" : "flex-row"
            }`}
          >
            <TouchableOpacity
              className=" ml-4"
              onPress={() => {
                handelSearch(searchValue);
              }}
            >
              <MaterialIcons
                name="search"
                className=""
                size={30}
                color="#838383"
              />
            </TouchableOpacity>
            <TextInput
              className={`p-4 rounded-md flex-1 font-intersb font-medium text-xl ${
                locale == "fr" ? "text-left" : "text-right"
              }`}
              placeholder={i18n.t("search")}
              placeholderTextColor={"#838383"}
              autoCapitalize="none"
              onChangeText={(value) => {
                handelSearchProduct(value);
                // setSearchQuery(value);
              }}
            />
            {/* Filter Button */}
            <TouchableOpacity
              className="mr-4 p-2"
              onPress={() => setShowFilterMenu(!showFilterMenu)}
            >
              <MaterialIcons
                name="filter-list"
                size={24}
                color={selectedFilter !== "all" ? "#007AFF" : "#838383"}
              />
            </TouchableOpacity>
          </View>

          {/* Filter Dropdown */}
          {showFilterMenu && (
            <Animated.View
              entering={FadeInDown.duration(200)}
              className="mt-2 bg-white rounded-xl  border border-gray-200 overflow-hidden z-[10]"
            >
              <TouchableOpacity
                className={`p-4 border-b border-gray-100 ${
                  selectedFilter === "all" ? "bg-blue-50" : ""
                }`}
                onPress={() => handleFilterChange("all")}
              >
                <Text
                  className={`text-base ${
                    selectedFilter === "all"
                      ? "text-blue-600 font-semibold"
                      : "text-gray-700"
                  }`}
                >
                  {i18n.t("all_products")}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                className={`p-4 border-b border-gray-100 ${
                  selectedFilter === "low_stock" ? "bg-blue-50" : ""
                }`}
                onPress={() => handleFilterChange("low_stock")}
              >
                <Text
                  className={`text-base ${
                    selectedFilter === "low_stock"
                      ? "text-blue-600 font-semibold"
                      : "text-gray-700"
                  }`}
                >
                  {i18n.t("low_stock")}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                className={`p-4 ${
                  selectedFilter === "high_value" ? "bg-blue-50" : ""
                }`}
                onPress={() => handleFilterChange("high_value")}
              >
                <Text
                  className={`text-base ${
                    selectedFilter === "high_value"
                      ? "text-blue-600 font-semibold"
                      : "text-gray-700"
                  }`}
                >
                  {i18n.t("high_value")}
                </Text>
              </TouchableOpacity>
            </Animated.View>
          )}

          {/* Sort Options */}
          <View className="flex-row items-center justify-between mt-3">
            <Text className="text-lg font-semibold text-gray-700">
              {i18n.t("sort_by")}
            </Text>
            <TouchableOpacity
              className="flex-row items-center bg-white rounded-xl px-4 py-2  border border-gray-200"
              onPress={() => setShowSortMenu(!showSortMenu)}
            >
              <Text className="text-base text-gray-700 mr-2">
                {selectedSort === "name_asc" && i18n.t("name_asc")}
                {selectedSort === "name_desc" && i18n.t("name_desc")}
                {selectedSort === "price_asc" && i18n.t("price_asc")}
                {selectedSort === "price_desc" && i18n.t("price_desc")}
                {selectedSort === "stock_asc" && i18n.t("stock_asc")}
                {selectedSort === "stock_desc" && i18n.t("stock_desc")}
              </Text>
              <MaterialIcons
                name={
                  showSortMenu ? "keyboard-arrow-up" : "keyboard-arrow-down"
                }
                size={20}
                color="#838383"
              />
            </TouchableOpacity>
          </View>

          {/* Sort Dropdown */}
          {showSortMenu && (
            <Animated.View
              entering={FadeInDown.duration(200)}
              className="mt-2 bg-white rounded-xl  border border-gray-200 overflow-hidden z-[10]"
            >
              <TouchableOpacity
                className={`p-3 border-b border-gray-100 ${
                  selectedSort === "name_asc" ? "bg-blue-50" : ""
                }`}
                onPress={() => handleSortChange("name_asc")}
              >
                <Text
                  className={`text-base ${
                    selectedSort === "name_asc"
                      ? "text-blue-600 font-semibold"
                      : "text-gray-700"
                  }`}
                >
                  {i18n.t("name_asc")}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                className={`p-3 border-b border-gray-100 ${
                  selectedSort === "name_desc" ? "bg-blue-50" : ""
                }`}
                onPress={() => handleSortChange("name_desc")}
              >
                <Text
                  className={`text-base ${
                    selectedSort === "name_desc"
                      ? "text-blue-600 font-semibold"
                      : "text-gray-700"
                  }`}
                >
                  {i18n.t("name_desc")}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                className={`p-3 border-b border-gray-100 ${
                  selectedSort === "price_asc" ? "bg-blue-50" : ""
                }`}
                onPress={() => handleSortChange("price_asc")}
              >
                <Text
                  className={`text-base ${
                    selectedSort === "price_asc"
                      ? "text-blue-600 font-semibold"
                      : "text-gray-700"
                  }`}
                >
                  {i18n.t("price_asc")}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                className={`p-3 border-b border-gray-100 ${
                  selectedSort === "price_desc" ? "bg-blue-50" : ""
                }`}
                onPress={() => handleSortChange("price_desc")}
              >
                <Text
                  className={`text-base ${
                    selectedSort === "price_desc"
                      ? "text-blue-600 font-semibold"
                      : "text-gray-700"
                  }`}
                >
                  {i18n.t("price_desc")}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                className={`p-3 border-b border-gray-100 ${
                  selectedSort === "stock_asc" ? "bg-blue-50" : ""
                }`}
                onPress={() => handleSortChange("stock_asc")}
              >
                <Text
                  className={`text-base ${
                    selectedSort === "stock_asc"
                      ? "text-blue-600 font-semibold"
                      : "text-gray-700"
                  }`}
                >
                  {i18n.t("stock_asc")}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                className={`p-3 ${
                  selectedSort === "stock_desc" ? "bg-blue-50" : ""
                }`}
                onPress={() => handleSortChange("stock_desc")}
              >
                <Text
                  className={`text-base ${
                    selectedSort === "stock_desc"
                      ? "text-blue-600 font-semibold"
                      : "text-gray-700"
                  }`}
                >
                  {i18n.t("stock_desc")}
                </Text>
              </TouchableOpacity>
            </Animated.View>
          )}
        </View>

        {/* the list with header */}

        {!stockLoading ? (
          <View className="w-full flex-1 my-4">
            {/* Table Header */}
            {displayedProducts.length > 0 && (
              <View
                className="w-full bg-blue-100 mb-2 gap-2 items-center flex flex-row-reverse justify-between rounded-xl py-3 px-2 border border-blue-200"
                style={{ backgroundColor: "#E0F2FE", borderColor: "#0EA5E9" }}
              >
                {/* Image Header - matches ProductItem photo container */}
                <View className="w-12 h-12 rounded-lg items-center justify-center">
                  <Text className="text-blue-800 font-bold text-xs text-center">
                    {i18n.t("image")}
                  </Text>
                </View>

                {/* Product Name Header - matches ProductItem flex-1 */}
                <Text className="text-blue-800 font-bold text-xs text-center flex-1">
                  {i18n.t("productName")}
                </Text>

                {/* Quantity Header - matches ProductItem quantity width */}
                <View
                  className="items-center justify-center"
                  style={{ minWidth: 40 }}
                >
                  <Text className="text-blue-800 font-bold text-xs text-center">
                    {i18n.t("quantity")}
                  </Text>
                </View>

                {/* Buy Price Header - matches ProductItem price width */}
                <View
                  className="items-center justify-center"
                  style={{ minWidth: 80 }}
                >
                  <Text className="text-blue-800 font-bold text-xs text-center">
                    {i18n.t("buyPrice")}
                  </Text>
                </View>

                {/* Total Value Header - matches ProductItem total width */}
                <View
                  className="items-center justify-center"
                  style={{ minWidth: 80 }}
                >
                  <Text className="text-blue-800 font-bold text-xs text-center">
                    {i18n.t("total")}
                  </Text>
                </View>
              </View>
            )}

            <FlatList
              data={displayedProducts}
              className="w-full flex-1"
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ flexGrow: 1 }}
              keyExtractor={(item) => item.id_product.toString()}
              removeClippedSubviews={true}
              maxToRenderPerBatch={10}
              windowSize={10}
              initialNumToRender={5}
              getItemLayout={(data, index) => ({
                length: 120, // Approximate item height
                offset: 120 * index,
                index,
              })}
              renderItem={({ item, index }) => (
                <Animated.View
                  entering={
                    index < 8
                      ? FadeInDown.delay(index * 60).duration(350).springify()
                      : undefined
                  }
                  layout={LinearTransition.springify()}
                >
                  <ProductItem
                    price_sell={parseFloat(item.price_sell)}
                    id={item.id_product}
                    price_bought={parseFloat(item.price_bought)}
                    barcode={item.barcode ? String(item.barcode) : ""}
                    productname={item.name}
                    photo={item.photo || ""}
                    stock_nbr={item.stock}
                    type_quantity={item.typeOfQuantity}
                    onDelete={deleteProduct}
                  />
                </Animated.View>
              )}
            />
          </View>
        ) : (
          <View className="flex-1 w-full justify-start items-center">
            <StockSkeletonLoader />
            
            {retryCount > 0 && (
              <TouchableOpacity
                onPress={handleRetry}
                className="mt-4 bg-blue-500 px-6 py-3 rounded-lg"
              >
                <Text className="text-white font-semibold">
                  {i18n.t("retry")} ({retryCount})
                </Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {stockError && (
          <View className="flex-1 items-center justify-center">
            <Text className="text-red-500 text-center mb-4 px-4">
              {stockError}
            </Text>
            <TouchableOpacity
              onPress={handleRetry}
              className="bg-red-500 px-6 py-3 rounded-lg"
            >
              <Text className="text-white font-semibold">
                {i18n.t("retry")}
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {!stockLoading && displayedProducts.length === 0 && (
          <View className="flex items-center justify-center ">
            <Text
              className="text-center mb-32
           text-2xl font-bold "
            >
              {searchValue ? i18n.t("noProductsFound") : i18n.t("noproducts")}
            </Text>
          </View>
        )}
      </View>
    </View>
  );
});

export default StockScreen;
